import { DocumentNode, gql, useMutation } from '@apollo/client';
import { Box, Button } from '@chakra-ui/react';
import QRCodeReact from 'qrcode.react';
import * as React from 'react';

import {
  APP_STORE_URL,
  detectClientPlatform,
  DevicePlatform,
  PLAY_STORE_URL,
  TRADEWING_APP_LOGO_SRC,
} from '@/modules/mobile-app/appStoreLinks';

import { DownloadApp_ConfirmDownloadApp } from './graphql-types/DownloadApp_ConfirmDownloadApp';
import { DownloadApp_TenantFragment } from './graphql-types/DownloadApp_TenantFragment';

import StepProgress from '../../../StepProgress';
import { ActivationModalFooter } from '../ActivationModalFooter';
import styles from './DownloadApp.module.css';

const DOWNLOAD_APP_COMPLETE = gql`
  mutation DownloadApp_ConfirmDownloadApp {
    confirmAppDownload {
      user {
        _id
        onboardingFlowState {
          isSkippedOrCompleted
          currentFlowState {
            currentStateIndex
            validOnboardingFlowStates
          }
        }
      }
    }
  }
`;

const AppleIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
    <path d="M16.4 12.6c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.15-2.8.85-3.5.85s-1.8-.8-3-.8c-1.5 0-3 .9-3.8 2.3-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.3 1.2 0 1.6-.75 3-.75s1.8.75 3 .72c1.3 0 2.1-1.1 2.8-2.2.9-1.3 1.2-2.5 1.3-2.6-.03 0-2.5-1-2.5-3.8zM14.2 5.8c.6-.8 1.1-1.9.95-3-.95.04-2.1.6-2.8 1.4-.6.7-1.1 1.8-1 2.9 1.1.1 2.2-.5 2.85-1.3z" />
  </svg>
);

const PlayIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#34d27b" d="M3.6 2.3c-.2.2-.3.6-.3 1v17.4c0 .4.1.8.3 1l9.7-9.7z" />
    <path fill="#ffd23f" d="m16.5 15.2-3.2-3.2 3.2-3.2 3.8 2.2c1 .6 1 1.6 0 2.2z" />
    <path fill="#ff4d5e" d="m13.3 12-9.7 9.7c.4.4 1 .4 1.7 0l11.2-6.5z" />
    <path fill="#3b95ff" d="M16.5 8.8 5.3 2.3c-.7-.4-1.3-.4-1.7 0l9.7 9.7z" />
  </svg>
);

export const DownloadApp: React.FC<{
  isLastStep: boolean;
  tenant: DownloadApp_TenantFragment;
  userName: string;
}> & {
  tenantFragment: DocumentNode;
} = ({ isLastStep, tenant, userName }) => {
  const [confirmAppDownload, { loading }] =
    useMutation<DownloadApp_ConfirmDownloadApp>(DOWNLOAD_APP_COMPLETE);

  // Detected after mount so server and client markup match.
  const [platform, setPlatform] = React.useState<DevicePlatform>('desktop');
  React.useEffect(() => {
    setPlatform(detectClientPlatform());
  }, []);
  const isPhone = platform !== 'desktop';

  const submit = async (): Promise<void> => {
    await confirmAppDownload();
  };

  // Scanning opens /get-app, which redirects to the right store for the phone.
  const origin =
    tenant.absoluteUrl ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const qrValue = origin ? `${origin}/get-app?src=web_popup` : '';

  const storeClass = (forPlatform: DevicePlatform): string =>
    platform === forPlatform
      ? `${styles.store} ${styles.storeHighlight}`
      : styles.store;

  return (
    <Box display="flex" flexDirection="column" overflow="auto" flexGrow="1">
      <section className={`${styles.act} ${isPhone ? styles.isMobile : ''}`}>
        <span className={styles.pill}>📱 Mobile app</span>
        <h2 className={styles.title}>
          Hi {userName || 'there'},<br />
          take <span className={styles.gradientText}>Tradewing</span> with you
        </h2>
        <p className={styles.subtitle}>
          Stay connected to the {tenant.acronym || tenant.displayName}{' '}
          community, events, members and sponsors on the go.
          {!isPhone && ' Scan once and we’ll open the right store for your phone.'}
        </p>

        {qrValue && (
          <div className={styles.qrWrap}>
            <div className={styles.qrCard}>
              <div className={styles.qrInner}>
                <QRCodeReact
                  value={qrValue}
                  size={132}
                  level="H"
                  fgColor="#0b1b3a"
                  bgColor="#ffffff"
                />
                <div className={styles.qrLogo}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={TRADEWING_APP_LOGO_SRC} alt="Tradewing" />
                </div>
              </div>
            </div>
            <div>
              <ul className={styles.steps}>
                <li>
                  <b className={styles.stepNum}>1</b>Open your phone camera
                </li>
                <li>
                  <b className={styles.stepNum}>2</b>Point it at the QR code
                </li>
                <li>
                  <b className={styles.stepNum}>3</b>Install &amp; sign in
                </li>
              </ul>
              <p className={styles.detect}>
                iPhone → App Store · Android → Google Play
              </p>
            </div>
          </div>
        )}

        <div className={styles.stores}>
          <a
            className={storeClass('ios')}
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <AppleIcon />
            <span>
              <small>Download on the</small>
              <strong>App Store</strong>
            </span>
          </a>
          <a
            className={storeClass('android')}
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <PlayIcon />
            <span>
              <small>Get it on</small>
              <strong>Google Play</strong>
            </span>
          </a>
        </div>

        {/* Small screens: compact row inside the card instead of the full-width footer */}
        <div className={styles.foot}>
          <StepProgress />
          <Button size="sm" onClick={submit} isLoading={loading}>
            {isLastStep ? 'Finish' : 'Next'}
          </Button>
        </div>
      </section>
      <ActivationModalFooter flexShrink="0" display={['none', null, 'flex']}>
        <StepProgress />
        <Button onClick={submit} isLoading={loading}>
          {isLastStep ? 'Finish' : 'Next'}
        </Button>
      </ActivationModalFooter>
    </Box>
  );
};
DownloadApp.tenantFragment = gql`
  fragment DownloadApp_TenantFragment on Tenant {
    _id
    acronym
    displayName
    absoluteUrl
  }
`;
